/* Canvas flight-chart generator — ported from the previous Good Tree theme and
   reworked for the Forest Manual theme.

   What's new vs the original:
   - Responsive: the canvas sizes its backing store to its actual rendered width,
     at the device pixel ratio, so it stays crisp on retina and reflows on resize.
   - Scale-aware: paddings, ring label font, dash lengths and line weights all scale
     with the rendered size instead of being fixed at one resolution.
   - Self-mounting: any <canvas data-flight-chart> with data-speed/glide/turn/fade is
     drawn on load and re-drawn on container resize / image decode. No inline script.
   - Palette-driven: stroke colors resolve from --gt-* CSS variables at draw time. */

(function () {
  'use strict';
  if (window.GTFlightChart) return; // guard against double-include

  // Base (reference) display settings — scaled per-render in scaleSettings().
  const BASE = {
    flightPath: { lineWidth: 3, lineCap: 'round', lineJoin: 'round' },
    chart: {
      minorDistance: 50,
      minChartDistance: 400,
      lineWidth: 1,
      dash: 5,
      padTop: 32,
      padBottom: 16,
      padSides: 16,
      labelFont: 14,
      labelOffsetX: 35,
      labelOffsetY: 4,
      refHeight: 480 // settings above are tuned for ~480px tall
    }
  };

  const FLIGHT_SCALING = {
    base_dis: 230, dps: 28.5, dpg: 11.2, hpt: 40, blpf: 50,
    slc: [[1,1.5],[6,1.0],[7,0.9],[8,0.8],[9,0.75],[10,0.7],[11,0.65],[12,0.6],[13,0.55],[14,0.5]]
  };

  const POWER_MAPPING = { Slower: 0.38, Normal: 0.46, Faster: 0.6 };
  const feetToMeters = (feet) => feet / 3.28084;

  // Resolve a throw-power input to its 0..1-ish factor. Accepts a preset name
  // ('Slower' | 'Normal' | 'Faster'), a number, or a numeric string like '0.52'
  // — the disc finder passes a continuous factor blended from throw distance and
  // arm speed (mph), so the simulated flight reflects both.
  const powerFactor = (power) => {
    if (typeof power === 'number' && isFinite(power)) return power;
    if (POWER_MAPPING[power] != null) return POWER_MAPPING[power];
    const n = parseFloat(power);
    return isFinite(n) ? n : POWER_MAPPING.Normal;
  };
  const powerCalc = (power) => 0.5 + powerFactor(power) * 0.5;
  const baseDistanceCalc = (speed, glide) => FLIGHT_SCALING.base_dis + speed * FLIGHT_SCALING.dps + glide * FLIGHT_SCALING.dpg;

  const interpolateSpeedFactor = (speed) => {
    const points = FLIGHT_SCALING.slc;
    let [x1, y1] = points[0];
    let [x2, y2] = points[points.length - 1];
    for (let i = 0; i < points.length - 1; i++) {
      if (speed >= points[i][0] && speed <= points[i + 1][0]) {
        [x1, y1] = points[i]; [x2, y2] = points[i + 1]; break;
      }
    }
    return y1 + ((speed - x1) * (y2 - y1)) / (x2 - x1);
  };

  const calculateMaxDistanceFt = (settings, speed, glide, power) => {
    const calculated = baseDistanceCalc(speed, glide) * powerCalc(power);
    const max = Math.ceil(calculated / 50) * 50;
    return max >= settings.chart.minChartDistance ? max : settings.chart.minChartDistance;
  };

  const pixelsPerUnit = (settings, height, distance) =>
    (height - settings.chart.padTop - settings.chart.padBottom) / distance;

  // Resolve a CSS custom property (e.g. --gt-mustard) to a concrete rgb() string.
  const resolveCssColor = (varName, fallback) => {
    try {
      const probe = document.createElement('span');
      probe.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none;color:var(' + varName + ')';
      document.body.appendChild(probe);
      const c = getComputedStyle(probe).color;
      document.body.removeChild(probe);
      return c && c !== 'rgba(0, 0, 0, 0)' ? c : fallback;
    } catch (e) {
      return fallback;
    }
  };

  // Build a per-render settings object scaled to the current logical height.
  function scaleSettings(logicalW, logicalH, colors, hand) {
    const s = logicalH / BASE.chart.refHeight;
    const round = (v) => Math.round(v * s);
    return {
      flightPath: {
        color: colors.path,
        lineWidth: Math.max(2, BASE.flightPath.lineWidth * s),
        lineCap: BASE.flightPath.lineCap,
        lineJoin: BASE.flightPath.lineJoin,
        style: hand === 'L' ? 'L' : 'R'
      },
      chart: {
        minorDistance: BASE.chart.minorDistance,
        minChartDistance: BASE.chart.minChartDistance,
        lineColor: colors.line,
        lineWidth: Math.max(1, BASE.chart.lineWidth * s),
        lineDash: [Math.max(2, BASE.chart.dash * s), Math.max(2, BASE.chart.dash * s)],
        padTop: round(BASE.chart.padTop),
        padBottom: round(BASE.chart.padBottom),
        padSides: round(BASE.chart.padSides),
        labelFont: Math.max(9, Math.round(BASE.chart.labelFont * s)),
        labelOffsetX: BASE.chart.labelOffsetX * s,
        labelOffsetY: BASE.chart.labelOffsetY * s
      }
    };
  }

  // maxFt (optional) overrides the chart scale — used by multi-disc overlays so
  // every path shares one coordinate system instead of each disc's own max.
  function calculateFlightPath(settings, speed, glide, turn, fade, power, w, h, maxFt) {
    const distanceMeters = feetToMeters(baseDistanceCalc(speed, glide) * powerCalc(power));
    let hss = turn * FLIGHT_SCALING.hpt * 2;
    let lsf = fade * FLIGHT_SCALING.blpf * interpolateSpeedFactor(speed) * 2;
    let airspeed = 0.7 + powerFactor(power) * 0.7;
    const turnsign = settings.flightPath.style === 'L' ? -1.0 : 1.0;
    const fadestart = 0.4 + (1.0 - airspeed ** 2) * 0.3;
    const impact = (1.0 - airspeed) / 5;
    const turnend = 0.8 - airspeed ** 2 * 0.36;
    const scaleFt = maxFt || calculateMaxDistanceFt(settings, speed, glide, power);
    const ppm = pixelsPerUnit(settings, h, feetToMeters(scaleFt));

    let ox = w / 2;
    let oy = h - settings.chart.padBottom;
    let vx = 0.0;
    const vy = -1.0;
    const deltav = 2.5 / (2.5 * distanceMeters);

    hss *= airspeed ** 4;
    lsf *= 1.0 / (airspeed * airspeed);

    const path = [];
    do {
      path.push({ x: ox, y: oy });
      const y = oy + vy * ppm;
      const x = ox + vx * 0.7 * ppm;
      airspeed -= deltav;
      if (airspeed > turnend) vx -= turnsign * (hss / 14000) * (turnend / airspeed);
      if (airspeed < fadestart) vx -= (turnsign * (lsf / 4000) * (fadestart - airspeed)) / fadestart;
      if (airspeed > 0.0) { ox = x; oy = y; }
    } while (airspeed > impact);

    return path;
  }

  function calculateControlPoints(p0, p1, p2, t) {
    t = t == null ? 0.5 : t;
    const d1 = Math.hypot(p1.x - p0.x, p1.y - p0.y);
    const d2 = Math.hypot(p2.x - p1.x, p2.y - p1.y);
    const d = d1 + d2 || 1;
    const fa = (t * d1) / d;
    const fb = (t * d2) / d;
    return {
      cp1: { x: p1.x - fa * (p2.x - p0.x), y: p1.y - fa * (p2.y - p0.y) },
      cp2: { x: p1.x + fb * (p2.x - p0.x), y: p1.y + fb * (p2.y - p0.y) }
    };
  }

  function drawFlightPath(ctx, settings, path) {
    ctx.strokeStyle = settings.flightPath.color;
    ctx.lineWidth = settings.flightPath.lineWidth;
    ctx.lineCap = settings.flightPath.lineCap;
    ctx.lineJoin = settings.flightPath.lineJoin;
    ctx.globalAlpha = 1;
    ctx.beginPath();
    ctx.moveTo(path[0].x, path[0].y);
    for (let i = 0; i < path.length - 2; i++) {
      const cps = calculateControlPoints(path[i], path[i + 1], path[i + 2]);
      ctx.bezierCurveTo(cps.cp1.x, cps.cp1.y, cps.cp2.x, cps.cp2.y, path[i + 1].x, path[i + 1].y);
    }
    const last = path[path.length - 1];
    ctx.lineTo(last.x, last.y);
    ctx.stroke();
  }

  function drawBackground(ctx, settings, w, h, maxFt, logo, fnLogo) {
    const c = settings.chart;
    ctx.beginPath();
    ctx.strokeStyle = c.lineColor;
    ctx.lineWidth = c.lineWidth;
    ctx.setLineDash(c.lineDash);
    ctx.moveTo(w / 2, c.padTop);
    ctx.lineTo(w / 2, h - c.padBottom);
    ctx.stroke();

    const minorMarks = maxFt / c.minorDistance;
    const ppf = pixelsPerUnit(settings, h, maxFt);
    const drawableY = h - c.padBottom;
    ctx.moveTo(c.padSides, drawableY);
    ctx.lineTo(w - c.padSides, drawableY);
    ctx.stroke();

    for (let i = 1; i <= minorMarks; i++) {
      const y = drawableY - i * c.minorDistance * ppf;
      const ft = i * c.minorDistance;
      ctx.beginPath();
      ctx.arc(w / 2, drawableY, drawableY - y, 1.375 * Math.PI, 1.625 * Math.PI, false);
      ctx.stroke();
      ctx.fillStyle = c.lineColor;
      ctx.font = 'bold ' + c.labelFont + "px 'JetBrains Mono', ui-monospace, monospace";
      ctx.fillText(ft + "'", w / 2 - c.labelOffsetX, y - c.labelOffsetY);
    }
    ctx.setLineDash([]);

    const placeLogo = (img, x) => {
      if (!img || !img.naturalWidth) return;
      const iw = w * 0.25;
      const ih = img.naturalHeight * (iw / img.naturalWidth);
      ctx.drawImage(img, x, drawableY - drawableY * 0.12, iw, ih);
    };
    placeLogo(logo, w * 0.08);
    placeLogo(fnLogo, w - w * 0.35);
  }

  // Size the canvas backing store to its ACTUAL rendered width (CSS px * DPR) so the
  // bitmap always matches what's shown — crisp at any size. To bound how large a chart
  // gets, cap the DISPLAYED width in CSS (e.g. max-width); capping the backing store
  // here would only blur a canvas that CSS had already stretched wider. data-max-width
  // is just the fallback width for when the canvas can't be measured yet (e.g. hidden).
  function sizeCanvas(canvas) {
    const aspect = parseFloat(canvas.dataset.aspect) || 0.75; // width / height
    const fallbackW = parseFloat(canvas.dataset.maxWidth) || 360;
    const host = canvas.parentElement || canvas;
    const avail = Math.round(canvas.getBoundingClientRect().width) ||
      host.clientWidth || canvas.clientWidth || fallbackW;
    const cssW = Math.max(160, avail);
    const cssH = Math.round(cssW / aspect);
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    canvas.width = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
    canvas.style.height = 'auto';
    return { cssW, cssH, dpr };
  }

  // Resolve & cache an Image from a URL on the canvas, redrawing once it decodes.
  // Returns the Image immediately; placeLogo() skips it until it has dimensions, so a
  // not-yet-loaded watermark just paints in on the redraw fired by its own load event.
  function imageFor(canvas, src, onLoad) {
    if (!src) return null;
    const cache = canvas.__fcImg || (canvas.__fcImg = {});
    let img = cache[src];
    if (!img) {
      img = new Image();
      cache[src] = img;
      if (onLoad) img.addEventListener('load', onLoad, { once: true });
      img.src = src;
    }
    return img;
  }

  // Public: draw a chart. Reads numbers from args (falls back to canvas data-*).
  function redrawFlightChart(canvas, logo, fnLogo, speed, glide, turn, fade, power) {
    if (!canvas || !canvas.getContext) return;
    const d = canvas.dataset || {};
    speed = speed != null ? speed : parseFloat(d.speed) || 0;
    glide = glide != null ? glide : parseFloat(d.glide) || 0;
    turn = turn != null ? turn : parseFloat(d.turn) || 0;
    fade = fade != null ? fade : parseFloat(d.fade) || 0;
    power = power != null ? power : (d.power || 'Normal');

    // Watermark / flight-number art: an explicit Image arg wins; otherwise resolve
    // from data-logo-src / data-fn-src URLs (set per-disc by the finder, per-product
    // by the flight-chart snippet). Reading from the dataset means the watermark also
    // survives resize redraws, which re-read these same attributes.
    const onArt = () => redrawFlightChart(canvas, null, null);
    if (logo == null) logo = imageFor(canvas, d.logoSrc, onArt);
    if (fnLogo == null) fnLogo = imageFor(canvas, d.fnSrc, onArt);

    const colors = {
      path: resolveCssColor(d.pathVar || '--gt-mustard', '#c89e2a'),
      line: resolveCssColor(d.lineVar || '--gt-sage-dk', '#8ca775')
    };

    const { cssW, cssH, dpr } = sizeCanvas(canvas);
    const settings = scaleSettings(cssW, cssH, colors, d.hand);

    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0); // draw in CSS pixels, crisp on retina
    ctx.clearRect(0, 0, cssW, cssH);

    drawBackground(ctx, settings, cssW, cssH, calculateMaxDistanceFt(settings, speed, glide, power), logo, fnLogo);
    const path = calculateFlightPath(settings, speed, glide, turn, fade, power, cssW, cssH);
    if (path.length >= 2) drawFlightPath(ctx, settings, path);
  }

  // Public: draw several discs' flight paths on ONE canvas with a shared scale.
  // discs: [{ speed, glide, turn, fade, color }] — color is a concrete CSS color
  // or a --gt-* variable name. Grid/labels use data-line-var as usual.
  function redrawFlightChartMulti(canvas, discs, power) {
    if (!canvas || !canvas.getContext) return;
    discs = (discs || []).filter((x) => x && [x.speed, x.glide, x.turn, x.fade].every(isFinite));
    const d = canvas.dataset || {};
    power = power != null ? power : (d.power || 'Normal');
    const lineColor = resolveCssColor(d.lineVar || '--gt-sage-dk', '#8ca775');

    const { cssW, cssH, dpr } = sizeCanvas(canvas);
    const settings = scaleSettings(cssW, cssH, { path: lineColor, line: lineColor }, d.hand);

    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssW, cssH);

    // Shared scale: the chart must fit the longest-flying disc.
    const maxFt = discs.reduce(
      (m, x) => Math.max(m, calculateMaxDistanceFt(settings, x.speed, x.glide, power)),
      settings.chart.minChartDistance
    );

    drawBackground(ctx, settings, cssW, cssH, maxFt, null, null);
    discs.forEach((x) => {
      const c = x.color || '--gt-mustard';
      settings.flightPath.color = c.indexOf('--') === 0 ? resolveCssColor(c, '#c89e2a') : c;
      const path = calculateFlightPath(settings, x.speed, x.glide, x.turn, x.fade, power, cssW, cssH, maxFt);
      if (path.length >= 2) drawFlightPath(ctx, settings, path);
    });
  }

  function mount(canvas) {
    if (canvas.__fcMounted) return;
    canvas.__fcMounted = true;
    // Watermark / flight-number art is resolved from data-logo-src / data-fn-src inside
    // redrawFlightChart, which also redraws once each image decodes.
    const draw = () => redrawFlightChart(canvas);

    draw();
    // Responsive: redraw when the container changes size.
    if (window.ResizeObserver && canvas.parentElement) {
      let raf;
      const ro = new ResizeObserver(() => { cancelAnimationFrame(raf); raf = requestAnimationFrame(draw); });
      ro.observe(canvas.parentElement);
    } else {
      window.addEventListener('resize', draw);
    }
  }

  function init(root) {
    (root || document).querySelectorAll('canvas[data-flight-chart]').forEach(mount);
  }

  window.GTFlightChart = { init: init, draw: redrawFlightChart, drawMulti: redrawFlightChartMulti };

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', () => init());
  window.addEventListener('load', () => init());
  // Re-scan after Shopify section re-renders in the theme editor.
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
